import {
  type Diagnostic,
  type RuleRegistry,
  type RoutineView,
  type RuleEvaluationContext,
  type ValidationRule,
} from '../types';

function dedupeDiagnostics(diags: Diagnostic[]): Diagnostic[] {
  const seen = new Set<string>();
  const out: Diagnostic[] = [];

  for (const d of diags) {
    const key = [d.code, d.stepId ?? '', d.fieldId ?? '', d.message].join('::');

    if (!seen.has(key)) {
      seen.add(key);
      out.push(d);
    }
  }

  // Return de-duplicated diagnostics
  return out;
}

function filterRulesForScope(
  registry: RuleRegistry,
  scope?: { stepId?: string; stepKind?: string },
): ValidationRule[] {
  if (!scope || !scope.stepId) {
    return registry;
  }

  const { stepKind } = scope;
  if (!stepKind) return registry;

  return registry.filter(
    (r) =>
      r.appliesTo === 'routine' ||
      !r.stepKinds ||
      r.stepKinds.includes(stepKind),
  );
}

export function runRules(
  registry: RuleRegistry,
  routine: RoutineView,
  ctx: RuleEvaluationContext,
  scope?: { stepId?: string; stepKind?: string; realtimeOnly?: boolean },
): Diagnostic[] {
  const rules = filterRulesForScope(
    scope?.realtimeOnly ? registry.filter((r) => r.realtime) : registry,
    scope,
  );

  const diagnostics: Diagnostic[] = [];

  for (const rule of rules) {
    if (rule.appliesTo === 'routine') {
      diagnostics.push(...(rule.run({ routine, ctx }) ?? []));
      continue;
    }

    // step-scoped
    if (scope?.stepId) {
      diagnostics.push(
        ...(rule.run({ routine, ctx, stepId: scope.stepId }) ?? []),
      );
    } else {
      for (const s of routine.steps) {
        // Effectively-suppressed steps (self or any ancestor suppressed) are
        // excluded from execution, so they are excluded from validation too.
        if (s.effectivelySuppressed) continue;

        // Only run step-scoped rules for matching step kinds
        if (Array.isArray((rule as any).stepKinds)) {
          const kinds = (rule as any).stepKinds as string[];
          if (!kinds.includes(s.stepKind)) continue;
        }

        diagnostics.push(...(rule.run({ routine, ctx, stepId: s.id }) ?? []));
      }
    }
  }

  return dedupeDiagnostics(diagnostics);
}
